import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Package, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProviderLayout } from '@/components/layouts/provider-layout';
import { CreateProductModal } from '@/components/modals/CreateProductModal';
import { useProducts } from '@/hooks/useProducts';
import { Product } from '@/services/products.service';
import { useTranslation } from 'react-i18next';

const STATUS_FILTER_OPTIONS = [
  { value: 'all', labelKey: 'products.statusFilters.all' },
  { value: 'active', labelKey: 'products.statusFilters.active' },
  { value: 'draft', labelKey: 'products.statusFilters.draft' },
  { value: 'published', labelKey: 'products.statusFilters.published' },
  { value: 'archived', labelKey: 'products.statusFilters.archived' },
  { value: 'suspended', labelKey: 'products.statusFilters.suspended' },
];

const VISIBILITY_FILTER_OPTIONS = [
  { value: 'all', labelKey: 'products.visibilityFilters.all' },
  { value: 'public', labelKey: 'products.visibilityFilters.public' },
  { value: 'private', labelKey: 'products.visibilityFilters.private' },
];

function getStatusBadgeVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case 'published':
    case 'active':
      return 'default';
    case 'draft':
      return 'secondary';
    case 'archived':
      return 'outline';
    case 'suspended':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export default function ProductList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Fetch products from API
  const { 
    data: productsResponse, 
    isLoading, 
    error,
    refetch 
  } = useProducts({
    search: searchQuery || undefined,
    status: statusFilter !== 'all' ? statusFilter as Product['status'] : undefined,
    visibility: visibilityFilter !== 'all' ? visibilityFilter as NonNullable<Product['visibility']> : undefined,
  });

  // Client-side filtering as fallback
  const filteredProducts = useMemo(() => {
    const products = productsResponse?.data ?? [];
    return products.filter((product) => {
      const matchesSearch = searchQuery === '' || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
      const productVisibility = product.visibility ?? 'public';
      const matchesVisibility = visibilityFilter === 'all' || productVisibility === visibilityFilter;
      
      return matchesSearch && matchesStatus && matchesVisibility;
    });
  }, [productsResponse?.data, searchQuery, statusFilter, visibilityFilter]);

  const localizedStatusOptions = useMemo(
    () =>
      STATUS_FILTER_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t],
  );

  const localizedVisibilityOptions = useMemo(
    () =>
      VISIBILITY_FILTER_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t],
  );

  const handleCreateProduct = () => {
    setShowCreateModal(true);
  };

  const handleProductClick = (productId: string) => {
    navigate(`/provider/products/${productId}`);
  };

  return (
    <ProviderLayout>
      <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{t('products.title')}</h1>
        <p className="text-muted-foreground">
          {t('products.subtitle')}
        </p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('products.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder={t('products.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            {localizedStatusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder={t('products.visibilityFilters.label')} />
          </SelectTrigger>
          <SelectContent>
            {localizedVisibilityOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button 
          variant="outline" 
          size="default"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </Button>
        <Button onClick={handleCreateProduct}>
          <Plus className="h-4 w-4 mr-2" />
          {t('products.createProductButton')}
        </Button>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-6">
              <Skeleton className="h-6 w-3/4 mb-2" />
              <Skeleton className="h-4 w-full mb-4" />
              <div className="flex items-center gap-2 mb-4">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-12" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>
            {t('errors.failedToLoadProducts')}{' '}
            {error.message}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => refetch()}
              className="ml-4"
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : filteredProducts.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('products.noProductsFound')}</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || statusFilter !== 'all' 
                ? t('products.tryAdjusting')
                : t('products.createFirstProduct')}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Button onClick={handleCreateProduct}>
                <Plus className="h-4 w-4 mr-2" />
                {t('products.createProductButton')}
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card 
              key={product.id} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleProductClick(product.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
              <CardTitle className="text-lg">{product.name}</CardTitle>
              <Badge variant={getStatusBadgeVariant(product.status)}>
                {product.status_label ||
                  t(`products.statuses.${product.status}`, {
                    defaultValue: product.status.charAt(0).toUpperCase() + product.status.slice(1),
                  })}
              </Badge>
            </div>
            <CardDescription className="line-clamp-2">
              {product.description}
            </CardDescription>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="outline">
                {product.visibility === 'private'
                  ? t('products.visibilityBadges.private')
                  : t('products.visibilityBadges.public')}
              </Badge>
            </div>
          </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Version and Endpoints */}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('products.version')}</span>
                    <span className="font-medium">{product.version}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('products.endpoints')}</span>
                    <span className="font-medium">{product.endpoint_count}</span>
                  </div>
                  
                  {/* Usage Stats */}
                  {(product.usage_stats || product.usage_statistics) && (
                    <div className="pt-3 border-t">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div className="text-2xl font-bold">
                            {formatNumber(
                              product.usage_statistics?.total_requests || 
                              product.usage_stats?.total_calls || 
                              0
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t('products.totalCalls')}
                          </div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {formatNumber(
                              product.usage_statistics?.requests_today || 
                              product.usage_stats?.last_24h || 
                              0
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t('products.today')}
                          </div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {(product.usage_statistics?.active_customers || 
                              product.usage_stats?.active_customers ||
                              product.customer_count ||
                              0)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t('products.customersLabel')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {product.tags && product.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-3">
                      {product.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
      
      {/* Create Product Modal */}
      <CreateProductModal 
        open={showCreateModal} 
        onOpenChange={setShowCreateModal} 
      />
    </ProviderLayout>
  );
}
